import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PORTFOLIO_MARGIN_MINIMUM_EQUITY } from "../../../constants/precision/margin";
import { AccountStateEnum, AuthContext } from "../../../contexts/AuthContext";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { useGetAccount } from "../../../hooks/api/account/useGetAccount";
import { useInviteStatus } from "../../../hooks/api/invite/useInviteStatus";
import { useOnboardingBalances } from "../../../hooks/onboarding/useOnboardingBalances";
import useWallet from "../../../hooks/wallet/useWallet";
import ConnectWalletModal from "../../ConnectWalletModal";
import { getCurrentChainConfig } from "../../../config/chains";
import { getAccountSurfaceFlags } from "../../../config/features";
import {
  AddressWrapper,
  MarginButton,
  MarginButtonWrapper,
} from "./style";
import { AccountDropdownStateEnum } from "./type";
import AccountButton from "./AccountButton";
import AccountModals from "./AccountModals";
import { IAccountDropdownProps } from "./types";


function AccountDropdown({ onSyncPWA }: IAccountDropdownProps) {
  const {
    accountApiKeyState,
    accountSigningKeyState,
    account,
    isAccountImported,
  } = useContext(AuthContext);
  const {
    showConnectModal,
    setShowConnectModal,
    connectWalletFlow,
    openDepositFlow,
  } = useContext(ConnectWalletContext);
  const navigate = useNavigate();
  const { connectedWallet, connector } = useWallet();
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showSubaccountsModal, setShowSubaccountsModal] = useState(false);
  const subaccountsSupported = (() => {
    const cfg = getCurrentChainConfig();
    return !!(cfg.supportsSubaccounts && cfg.gasless && cfg.relayerUrl && cfg.contracts.perpetualDex?.router);
  })();
  const [depositModalDismissed, setDepositModalDismissed] = useState(false);
  const [autoDepositModalOpen, setAutoDepositModalOpen] = useState(false);

  const { data: accountData, showOnboarding } = useGetAccount();
  const { showDerivativeUi } = getAccountSurfaceFlags(accountData?.positions);
  const { data: inviteStatus } = useInviteStatus();
  const { t } = useTranslation("app", { keyPrefix: "shared.AccountDropdown" });
  const inviteRequired = Boolean(
    inviteStatus?.invite_required && !inviteStatus.allowed
  );

  const onboardingActive = useMemo(
    () =>
      Boolean(
        account &&
          (accountApiKeyState !== AccountStateEnum.OK ||
            accountSigningKeyState !== AccountStateEnum.OK ||
            inviteRequired ||
            showOnboarding === true)
      ),
    [account, accountApiKeyState, accountSigningKeyState, inviteRequired, showOnboarding]
  );
  const { eoaUsdcBalance, vaultBalance } =
    useOnboardingBalances(onboardingActive);
  const accountVaultBalance = useMemo(() => {
    if (!accountData) {
      return undefined;
    }
    const parsedBalance = Number(
      accountData.balance ||
        accountData.equity ||
        accountData.available_balance ||
        "0"
    );
    return Number.isFinite(parsedBalance) ? parsedBalance : undefined;
  }, [accountData]);
  const effectiveVaultBalance = vaultBalance ?? accountVaultBalance;

  useEffect(() => {
    setDepositModalDismissed(false);
    setAutoDepositModalOpen(false);
  }, [account]);

  useEffect(() => {
    if (
      (effectiveVaultBalance !== undefined && effectiveVaultBalance > 0) ||
      eoaUsdcBalance === 0
    ) {
      setDepositModalDismissed(false);
    }
  }, [eoaUsdcBalance, effectiveVaultBalance]);

  useEffect(() => {
    if (
      onboardingActive &&
      !showConnectModal &&
      !depositModalDismissed &&
      eoaUsdcBalance !== undefined &&
      effectiveVaultBalance !== undefined &&
      eoaUsdcBalance > 0 &&
      effectiveVaultBalance <= 0
    ) {
      setAutoDepositModalOpen(true);
    }
  }, [
    depositModalDismissed,
    eoaUsdcBalance,
    effectiveVaultBalance,
    onboardingActive,
    showConnectModal,
  ]);

  useEffect(() => {
    if (
      autoDepositModalOpen &&
      showConnectModal &&
      connectWalletFlow === "deposit" &&
      effectiveVaultBalance !== undefined &&
      effectiveVaultBalance > 0
    ) {
      setAutoDepositModalOpen(false);
      setShowConnectModal(false);
    }
  }, [
    autoDepositModalOpen,
    connectWalletFlow,
    effectiveVaultBalance,
    setShowConnectModal,
    showConnectModal,
  ]);

  const accountDropdownState: AccountDropdownStateEnum = useMemo(() => {
    if (account) {
      return AccountDropdownStateEnum.WALLET_CONNECTED;
    }
    return AccountDropdownStateEnum.WALLET_DISCONNECTED;
  }, [account]);

  const buttonContent = useMemo(
    () => (
      <AccountButton
        accountDropdownState={accountDropdownState}
        accountApiKeyState={accountApiKeyState}
        accountSigningKeyState={accountSigningKeyState}
        inviteRequired={inviteRequired}
        accountData={accountData}
        account={account}
        connectedWallet={connectedWallet}
        connector={connector}
        isAccountImported={isAccountImported}
        t={t}
        setShowConnectModal={setShowConnectModal}
        setShowAccountModal={setShowAccountModal}
      />
    ),
    [
      isAccountImported,
      account,
      accountApiKeyState,
      accountData?.in_liquidation,
      accountDropdownState,
      accountSigningKeyState,
      inviteRequired,
      setShowConnectModal,
      t,
    ]
  );

  const onHideModal = useCallback(
    () => {
      if (connectWalletFlow === "deposit") {
        setDepositModalDismissed(true);
      }
      setAutoDepositModalOpen(false);
      setShowConnectModal(false);
    },
    [connectWalletFlow, setShowConnectModal]
  );

  return (
    <>
      {showConnectModal && (
        <ConnectWalletModal show={showConnectModal} onHide={onHideModal} />
      )}
      {showDerivativeUi &&
      Number(accountData?.equity) >= PORTFOLIO_MARGIN_MINIMUM_EQUITY ? (
        <MarginButtonWrapper>
          <MarginButton
            onClick={() => navigate("/settings/portfolio-margin")}
            isActive={accountData?.portfolio}
          >
            {t("pm")}
          </MarginButton>
        </MarginButtonWrapper>
      ) : null}
      <AddressWrapper
        accountDropdownState={accountDropdownState}
        tradingEnabled={
          accountSigningKeyState === AccountStateEnum.OK && !inviteRequired
        }
      >
        {buttonContent}
        {account && (
          <AccountModals
            showAccountModal={showAccountModal}
            setShowAccountModal={setShowAccountModal}
            onSyncPWA={onSyncPWA}
            openDepositFlow={openDepositFlow}
            setShowWithdrawModal={setShowWithdrawModal}
            setShowTransferModal={setShowTransferModal}
            subaccountsSupported={subaccountsSupported}
            setShowSubaccountsModal={setShowSubaccountsModal}
            setShowConnectModal={setShowConnectModal}
            showSubaccountsModal={showSubaccountsModal}
            showWithdrawModal={showWithdrawModal}
            accountData={accountData}
            showTransferModal={showTransferModal}
          />
        )}
      </AddressWrapper>
    </>
  );
}

export type { IAccountDropdownProps } from "./types";

export default AccountDropdown;
